import ExcelJS from "exceljs";
import { format, getISOWeek } from "date-fns";
import { COMPANY_NAME, COMPANY_RIF } from "./company.js";

const GREEN = "1F6B4A";
const GREEN_SOFT = "E7EFE9";
const GREEN_ROW = "D4EBDD";
const WHITE = "FFFFFF";
const LINE = "D1D5DB";

const FMT_L = "#,##0.00";
const FMT_KG = "#,##0.00";
const FMT_AVG = '#,##0.00" L/kg"';
const FMT_INT = "#,##0";
const FMT_DATE = "dd/mm/yyyy";

const thin = { style: "thin", color: { argb: LINE } };
const border = { top: thin, left: thin, bottom: thin, right: thin };

const fillSolid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

const dateKey = (value) => String(value || "").slice(0, 10);

const n2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const productName = (value) => {
  if (value && typeof value === "object") return value.name || "";
  return String(value || "");
};

const providerName = (alloc) => {
  const p = alloc?.provider;
  if (p && typeof p === "object") return p.name || "";
  return "";
};

function styleCell(cell, opts = {}) {
  cell.font = opts.font || { name: "Calibri", size: 10, color: { argb: "1F2937" } };
  if (opts.fill) cell.fill = fillSolid(opts.fill);
  cell.alignment = opts.alignment || { vertical: "middle" };
  cell.border = opts.border === false ? undefined : border;
  if (opts.numFmt) cell.numFmt = opts.numFmt;
}

function headerFont(size = 10) {
  return { name: "Calibri", size, bold: true, color: { argb: WHITE } };
}

function titleFont(size = 16) {
  return { name: "Calibri", size, bold: true, color: { argb: GREEN } };
}

function applyHeaderRow(row, { fill = GREEN, fontSize = 10, wrap = false } = {}) {
  row.height = wrap ? 28 : 20;
  row.eachCell((cell) => {
    styleCell(cell, {
      font: headerFont(fontSize),
      fill,
      alignment: { vertical: "middle", horizontal: "center", wrapText: wrap },
    });
  });
}

function applyBodyRow(row, { fill, bold = false } = {}) {
  row.height = 18;
  row.eachCell((cell) => {
    const current = cell.font || {};
    styleCell(cell, {
      font: { name: "Calibri", size: 10, bold, color: current.color || { argb: "1F2937" } },
      fill,
      alignment: cell.alignment?.horizontal
        ? { vertical: "middle", horizontal: cell.alignment.horizontal }
        : { vertical: "middle" },
      numFmt: cell.numFmt,
    });
  });
}

async function downloadXlsx(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function weekMeta(data) {
  const days = data?.days || [];
  const start = dateKey(days[0]?.date || data?.startOfWeek);
  const end = dateKey(days[6]?.date || data?.endOfWeek);
  const startLabel = start ? format(new Date(`${start}T12:00:00`), "dd/MM/yyyy") : "";
  const endLabel = end ? format(new Date(`${end}T12:00:00`), "dd/MM/yyyy") : "";
  const weekNo = end ? getISOWeek(new Date(`${end}T12:00:00`)) : "";
  const generatedAt = format(new Date(), "dd/MM/yyyy HH:mm");
  return {
    days,
    start,
    end,
    weekNo,
    generatedAt,
    periodLabel: `Jueves ${startLabel} — Miércoles ${endLabel}`,
  };
}

function writeBanner(sheet, { subtitle, periodLabel, weekNo, generatedAt, extra }) {
  sheet.getCell("A1").value = COMPANY_NAME;
  sheet.getCell("A1").font = titleFont(16);
  sheet.getCell("A2").value = `RIF ${COMPANY_RIF}`;
  sheet.getCell("A2").font = { name: "Calibri", size: 10, color: { argb: "4B5563" } };
  sheet.getCell("A3").value = subtitle;
  sheet.getCell("A3").font = { name: "Calibri", size: 13, bold: true, color: { argb: GREEN } };
  sheet.getCell("A4").value = weekNo ? `${periodLabel}    Semana ISO ${weekNo}` : periodLabel;
  sheet.getCell("A4").font = { name: "Calibri", size: 10 };
  sheet.getCell("A5").value = extra ? `Generado: ${generatedAt}    ${extra}` : `Generado: ${generatedAt}`;
  sheet.getCell("A5").font = { name: "Calibri", size: 9, color: { argb: "6B7280" } };
}

function setupSheet(sheet, { frozenRows = 7, frozenCols = 1, landscape = true } = {}) {
  sheet.views = [{ state: "frozen", ySplit: frozenRows, xSplit: frozenCols, showGridLines: false }];
  sheet.pageSetup = {
    orientation: landscape ? "landscape" : "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  };
  sheet.headerFooter.oddFooter = `&L${COMPANY_NAME}&C&P / &N&RQuesos semanal`;
}

function excelDate(value) {
  const key = dateKey(value);
  if (key.length < 10) return null;
  const d = new Date(`${key}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function allocationSummary(allocations) {
  return (allocations || [])
    .map((alloc) => {
      const name = providerName(alloc) || "Proveedor";
      const liters = n2(alloc.liters);
      const type = alloc.milkType === "bufalo" ? "B" : "V";
      return `${name} ${liters} L ${type}`;
    })
    .filter(Boolean)
    .join("; ");
}

function writeResumen(workbook, { data, meta }) {
  const sheet = workbook.addWorksheet("Resumen", { properties: { tabColor: { argb: GREEN } } });
  sheet.columns = [
    { width: 16 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
  ];
  const totals = data.totals || {};
  writeBanner(sheet, {
    subtitle: "REPORTE SEMANAL DE QUESOS",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const kpiLabels = sheet.getRow(7);
  ["Recibido (L)", "Usado (L)", "Restante (L)", "Kg", "Promedio", "Quesos", "Producciones"].forEach(
    (label, i) => {
      kpiLabels.getCell(i + 1).value = label;
    },
  );
  applyHeaderRow(kpiLabels);

  const kpi = sheet.getRow(8);
  const values = [
    { v: n2(totals.received), fmt: FMT_L },
    { v: n2(totals.used), fmt: FMT_L },
    { v: n2(totals.remaining), fmt: FMT_L },
    { v: n2(totals.kg), fmt: FMT_KG },
    { v: totals.promedio == null ? null : n2(totals.promedio), fmt: FMT_AVG },
    { v: Number(totals.cheeseCount) || 0, fmt: FMT_INT },
    { v: (data.productions || []).length, fmt: FMT_INT },
  ];
  values.forEach((item, i) => {
    const cell = kpi.getCell(i + 1);
    cell.value = item.v;
    if (item.v != null) cell.numFmt = item.fmt;
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });
  applyBodyRow(kpi, { fill: GREEN_ROW, bold: true });
  kpi.height = 22;

  sheet.getCell("A10").value = "LECHE POR TIPO";
  sheet.getCell("A10").font = { name: "Calibri", size: 11, bold: true, color: { argb: GREEN } };

  const milkHeader = sheet.getRow(11);
  ["Concepto", "Vaca (L)", "Búfala (L)", "Total (L)"].forEach((label, i) => {
    milkHeader.getCell(i + 1).value = label;
  });
  applyHeaderRow(milkHeader);

  [
    {
      label: "Recibido",
      vaca: totals.receivedVaca,
      bufalo: totals.receivedBufalo,
      total: totals.received,
    },
    {
      label: "Usado en quesos",
      vaca: totals.usedVaca,
      bufalo: totals.usedBufalo,
      total: totals.used,
      fill: "F8FAFC",
    },
    {
      label: "Restante",
      vaca: totals.remainingVaca,
      bufalo: totals.remainingBufalo,
      total: totals.remaining,
      fill: GREEN_SOFT,
      bold: true,
    },
  ].forEach((item, index) => {
    const row = sheet.getRow(12 + index);
    row.getCell(1).value = item.label;
    row.getCell(2).value = n2(item.vaca);
    row.getCell(3).value = n2(item.bufalo);
    row.getCell(4).value = n2(item.total);
    [2, 3, 4].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });
    applyBodyRow(row, { fill: item.fill, bold: item.bold });
  });

  sheet.getCell("A16").value =
    `Productos con producción: ${(data.byProduct || []).filter((p) => Number(p.batches) > 0).length}` +
    `    Proveedores con leche: ${(data.providers || []).length}`;
  sheet.getCell("A16").font = { name: "Calibri", size: 9, color: { argb: "6B7280" } };

  setupSheet(sheet, { frozenRows: 8, frozenCols: 1, landscape: false });
}

function writePorProducto(workbook, { data, meta }) {
  const sheet = workbook.addWorksheet("Por producto", { properties: { tabColor: { argb: "0F766E" } } });
  sheet.columns = [
    { width: 28 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
    { width: 12 },
  ];
  writeBanner(sheet, {
    subtitle: "QUESOS POR PRODUCTO",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const header = sheet.getRow(7);
  [
    "Producto",
    "Vaca (L)",
    "Búfala (L)",
    "Total L",
    "Kg",
    "Promedio",
    "Quesos",
    "Lotes",
  ].forEach((label, i) => {
    header.getCell(i + 1).value = label;
  });
  applyHeaderRow(header);

  const rows = data.byProduct || [];
  rows.forEach((item, index) => {
    const row = sheet.getRow(8 + index);
    const active = Number(item.batches) > 0 || n2(item.totalLiters) > 0.01;
    row.getCell(1).value = item.name || "";
    row.getCell(2).value = n2(item.litersVaca);
    row.getCell(3).value = n2(item.litersBufalo);
    row.getCell(4).value = n2(item.totalLiters);
    row.getCell(5).value = n2(item.kg);
    row.getCell(6).value = item.promedio == null ? null : n2(item.promedio);
    row.getCell(7).value = Number(item.cheeseCount) || 0;
    row.getCell(8).value = Number(item.batches) || 0;

    [2, 3, 4].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
    });
    row.getCell(5).numFmt = FMT_KG;
    if (item.promedio != null) row.getCell(6).numFmt = FMT_AVG;
    row.getCell(7).numFmt = FMT_INT;
    row.getCell(8).numFmt = FMT_INT;
    [2, 3, 4, 5, 6, 7, 8].forEach((col) => {
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });
    applyBodyRow(row, {
      fill: active ? "ECFDF5" : index % 2 === 0 ? WHITE : "F8FAFC",
    });
  });

  if (rows.length > 0) {
    const first = 8;
    const last = 7 + rows.length;
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTALES";
    ["B", "C", "D", "E", "G", "H"].forEach((letter) => {
      total.getCell(letter).value = { formula: `SUM(${letter}${first}:${letter}${last})` };
    });
    ["B", "C", "D"].forEach((letter) => {
      total.getCell(letter).numFmt = FMT_L;
    });
    total.getCell("E").numFmt = FMT_KG;
    total.getCell("G").numFmt = FMT_INT;
    total.getCell("H").numFmt = FMT_INT;
    const totLiters = rows.reduce((s, r) => s + n2(r.totalLiters), 0);
    const totKg = rows.reduce((s, r) => s + n2(r.kg), 0);
    total.getCell(6).value = totKg > 0.009 ? n2(totLiters / totKg) : null;
    if (totKg > 0.009) total.getCell(6).numFmt = FMT_AVG;
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: last, column: 8 } };
  } else {
    sheet.getCell("A8").value = "No hay productos cargados en el sistema.";
  }

  setupSheet(sheet, { frozenRows: 7, frozenCols: 1 });
}

function writeDetalle(workbook, { data, meta }) {
  const sheet = workbook.addWorksheet("Detalle producciones", {
    properties: { tabColor: { argb: "B45309" } },
  });
  sheet.columns = [
    { width: 12 },
    { width: 10 },
    { width: 28 },
    { width: 22 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 14 },
    { width: 10 },
    { width: 42 },
  ];
  writeBanner(sheet, {
    subtitle: "DETALLE DE PRODUCCIONES",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const header = sheet.getRow(7);
  [
    "Fecha",
    "Día",
    "Nombre",
    "Producto",
    "Vaca (L)",
    "Búfala (L)",
    "Total L",
    "Kg",
    "Promedio",
    "Quesos",
    "Asignación leche",
  ].forEach((label, i) => {
    header.getCell(i + 1).value = label;
  });
  applyHeaderRow(header, { wrap: true });

  const dayLabels = {};
  (meta.days || []).forEach((day) => {
    dayLabels[dateKey(day.date)] = day.label || day.shortLabel || "";
  });

  const productions = [...(data.productions || [])].sort((a, b) => {
    const da = dateKey(a.date);
    const db = dateKey(b.date);
    if (da !== db) return da.localeCompare(db);
    const pa = productName(a.product);
    const pb = productName(b.product);
    if (pa !== pb) return pa.localeCompare(pb, "es");
    return String(a.name || "").localeCompare(String(b.name || ""), "es");
  });

  productions.forEach((prod, index) => {
    const key = dateKey(prod.date);
    const row = sheet.getRow(8 + index);
    row.getCell(1).value = excelDate(prod.date);
    row.getCell(1).numFmt = FMT_DATE;
    row.getCell(2).value = dayLabels[key] || "";
    row.getCell(3).value = prod.name || "—";
    row.getCell(4).value = productName(prod.product);
    row.getCell(5).value = n2(prod.litersVaca);
    row.getCell(6).value = n2(prod.litersBufalo);
    row.getCell(7).value = n2(prod.totalLiters);
    row.getCell(8).value = n2(prod.kg);
    row.getCell(9).value = prod.promedio == null ? null : n2(prod.promedio);
    row.getCell(10).value = Number(prod.cheeseCount) || 0;
    row.getCell(11).value = allocationSummary(prod.allocations);

    [5, 6, 7].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
    });
    row.getCell(8).numFmt = FMT_KG;
    if (prod.promedio != null) row.getCell(9).numFmt = FMT_AVG;
    row.getCell(10).numFmt = FMT_INT;
    [5, 6, 7, 8, 9, 10].forEach((col) => {
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });
    applyBodyRow(row, { fill: index % 2 === 0 ? WHITE : "F8FAFC" });
  });

  if (productions.length > 0) {
    const first = 8;
    const last = 7 + productions.length;
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTALES";
    ["E", "F", "G", "H", "J"].forEach((letter) => {
      total.getCell(letter).value = { formula: `SUM(${letter}${first}:${letter}${last})` };
    });
    ["E", "F", "G"].forEach((letter) => {
      total.getCell(letter).numFmt = FMT_L;
    });
    total.getCell("H").numFmt = FMT_KG;
    total.getCell("J").numFmt = FMT_INT;
    const totLiters = productions.reduce((s, p) => s + n2(p.totalLiters), 0);
    const totKg = productions.reduce((s, p) => s + n2(p.kg), 0);
    total.getCell(9).value = totKg > 0.009 ? n2(totLiters / totKg) : null;
    if (totKg > 0.009) total.getCell(9).numFmt = FMT_AVG;
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: last, column: 11 } };
  } else {
    sheet.getCell("A8").value = "No hay producciones en esta semana.";
  }

  setupSheet(sheet, { frozenRows: 7, frozenCols: 3 });
}

function writeProveedores(workbook, { data, meta }) {
  const sheet = workbook.addWorksheet("Leche proveedores", {
    properties: { tabColor: { argb: "1D4ED8" } },
  });
  sheet.columns = [
    { width: 28 },
    { width: 13 },
    { width: 13 },
    { width: 12 },
    { width: 12 },
    { width: 13 },
    { width: 13 },
  ];
  writeBanner(sheet, {
    subtitle: "LECHE DE PROVEEDORES — USO EN QUESOS",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const header = sheet.getRow(7);
  [
    "Proveedor",
    "Recibido V",
    "Recibido B",
    "Usado V",
    "Usado B",
    "Restante V",
    "Restante B",
  ].forEach((label, i) => {
    header.getCell(i + 1).value = label;
  });
  applyHeaderRow(header);

  const providers = data.providers || [];
  providers.forEach((item, index) => {
    const row = sheet.getRow(8 + index);
    const remaining = n2(item.remaining);
    let fill = index % 2 === 0 ? WHITE : "F8FAFC";
    if (remaining > 0.01) fill = "FEF9C3";
    else if (n2(item.used) > 0.01) fill = "ECFDF5";
    else fill = "FEF2F2";

    row.getCell(1).value = item.name || "";
    row.getCell(2).value = n2(item.receivedVaca);
    row.getCell(3).value = n2(item.receivedBufalo);
    row.getCell(4).value = n2(item.usedVaca);
    row.getCell(5).value = n2(item.usedBufalo);
    row.getCell(6).value = n2(item.remainingVaca);
    row.getCell(7).value = n2(item.remainingBufalo);
    [2, 3, 4, 5, 6, 7].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });
    applyBodyRow(row, { fill });
  });

  if (providers.length > 0) {
    const first = 8;
    const last = 7 + providers.length;
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTALES";
    ["B", "C", "D", "E", "F", "G"].forEach((letter) => {
      total.getCell(letter).value = { formula: `SUM(${letter}${first}:${letter}${last})` };
      total.getCell(letter).numFmt = FMT_L;
    });
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: last, column: 7 } };
  } else {
    sheet.getCell("A8").value = "No hay litrajes cargados en esta semana.";
  }

  setupSheet(sheet, { frozenRows: 7, frozenCols: 1 });
}

export async function exportCheeseWeekExcel(data) {
  if (!data) throw new Error("No hay datos de la semana para exportar.");
  const meta = weekMeta(data);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = COMPANY_NAME;
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.title = `Quesos semanal ${meta.periodLabel}`;

  writeResumen(workbook, { data, meta });
  writePorProducto(workbook, { data, meta });
  writeDetalle(workbook, { data, meta });
  writeProveedores(workbook, { data, meta });

  await downloadXlsx(workbook, `quesos_semana_${meta.start || "reporte"}.xlsx`);
}
